import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  completeClient360Task,
  createClient360Task,
  fetchResponsibleOptions,
  fetchTimeline,
  quickLogCall,
  type Client360QuickLogPayload,
  type Client360TaskCompletePayload,
  type Client360TaskCreatePayload,
} from "@/api/client360Tasks";

const STALE = 30_000;

/**
 * 3A Task Core — invalidate everything a task/call mutation can touch:
 * the holding timeline + tasks tab + overview + envelope, plus the global
 * F3 lists + home queue.
 */
function invalidate3A(qc: ReturnType<typeof useQueryClient>, holdingId: string) {
  qc.invalidateQueries({ queryKey: ["client360Timeline", holdingId] });
  qc.invalidateQueries({ queryKey: ["clientTab", holdingId, "tasks"] });
  qc.invalidateQueries({ queryKey: ["clientTab", holdingId, "activities"] });
  qc.invalidateQueries({ queryKey: ["client360Overview", holdingId] });
  qc.invalidateQueries({ queryKey: ["client360", holdingId] });
  qc.invalidateQueries({ queryKey: ["tasks"] });
  qc.invalidateQueries({ queryKey: ["homeSummary"] });
}

export function useResponsibleOptions(enabled = true) {
  return useQuery({
    queryKey: ["client360ResponsibleOptions"],
    queryFn: ({ signal }) => fetchResponsibleOptions(signal),
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useClient360Timeline(holdingId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["client360Timeline", holdingId],
    queryFn: ({ signal }) => fetchTimeline(holdingId as string, {}, signal),
    enabled: !!holdingId && enabled,
    staleTime: STALE,
  });
}

export function useCreateClient360Task(holdingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Client360TaskCreatePayload) => createClient360Task(payload),
    onSuccess: () => invalidate3A(qc, holdingId),
  });
}

export function useCompleteClient360Task(holdingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Client360TaskCompletePayload }) =>
      completeClient360Task(id, body),
    onSuccess: () => invalidate3A(qc, holdingId),
  });
}

export function useQuickLogCall(holdingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Client360QuickLogPayload) => quickLogCall(payload),
    onSuccess: () => invalidate3A(qc, holdingId),
  });
}
